/** @format */

import 'src/i18n';
import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { resolveSelectedLanguage } from 'src/utils/locale';
import { resolveExtranetApiLanguageCode, resolveExtranetSelectableLocale } from 'src/utils/extranetLanguages';
import type { ContentListApiResponse, EditorsResponse } from 'src/types';

const initialState = {
  keyword: [],
  dateFrom: null,
  dateTo: null,
  selectedStatus: null,
  selectedEditors: [],
  selectedLanguage: resolveExtranetSelectableLocale(resolveSelectedLanguage()),
  onlyOwn: false,
  selectedSortBy: 'latest',
  editorsLoading: false,
  editors: [],
  editorsError: null,
  resultsLoading: false,
  resultsAppending: false,
  results: [],
  resultsNextCursor: null,
  resultsTotal: 0,
  resultsError: null,
  deleteLoading: false,
  deleteError: null,
  deletingContentId: null,
  togglePublishedLoading: false,
  togglePublishedError: null,
  togglingContentId: null
};

const slice = createSlice({
  name: 'contents',
  initialState,
  reducers: {
    setKeyword(state, action) {
      state.keyword = normalizeKeywords(action.payload);
    },
    setDateRange(state, action) {
      state.dateFrom = action?.payload?.dateFrom || null;
      state.dateTo = action?.payload?.dateTo || null;
    },
    setSelectedStatus(state, action) {
      if (typeof action.payload === 'boolean' || action.payload === null) {
        state.selectedStatus = action.payload;
      }
    },
    setSelectedEditors(state, action) {
      state.selectedEditors = Array.isArray(action.payload) ? action.payload.filter(Boolean) : [];
    },
    setSelectedLanguage(state, action) {
      state.selectedLanguage = resolveExtranetSelectableLocale(action.payload, state.selectedLanguage || 'en');
    },
    setOnlyOwn(state, action) {
      state.onlyOwn = Boolean(action.payload);
    },
    setSelectedSortBy(state, action) {
      state.selectedSortBy = action.payload || 'latest';
    },
    getEditors(state) {
      state.editorsLoading = true;
      state.editorsError = null;
    },
    getEditorsSuccess(state, action) {
      state.editorsLoading = false;
      state.editors = Array.isArray(action.payload) ? action.payload : [];
    },
    getEditorsFailure(state, action) {
      state.editorsLoading = false;
      state.editorsError = action.payload;
    },
    resetFilters(state) {
      state.keyword = [];
      state.dateFrom = null;
      state.dateTo = null;
      state.selectedStatus = null;
      state.selectedEditors = [];
      state.selectedLanguage = resolveExtranetSelectableLocale(resolveSelectedLanguage());
      state.onlyOwn = false;
      state.selectedSortBy = 'latest';
    },
    getContents(state, action) {
      state.resultsLoading = true;
      state.resultsAppending = !!action?.payload?.append;
      state.resultsError = null;
    },
    getContentsSuccess(state, action) {
      const { items, nextCursor, total, append } = action.payload || {};
      const safeItems = Array.isArray(items) ? items : [];
      const currentItems = Array.isArray(state.results) ? state.results : [];
      state.results = append ? [...currentItems, ...safeItems] : safeItems;
      state.resultsNextCursor = nextCursor ?? null;
      state.resultsTotal = typeof total === 'number' ? total : state.resultsTotal;
      state.resultsLoading = false;
      state.resultsAppending = false;
    },
    getContentsFailure(state, action) {
      state.resultsLoading = false;
      state.resultsAppending = false;
      state.resultsError = action.payload;
    },
    deleteContentRequest: (state, action) => {
      state.deleteLoading = true;
      state.deleteError = null;
      state.deletingContentId = action.payload.contentId;
    },

    deleteContentSuccess: (state, action) => {
      state.deleteLoading = false;
      state.deleteError = null;
      state.deletingContentId = null;
      // optional: remove item from local state
      state.results = state.results.filter(item => item.id !== action.payload.contentId);
    },

    deleteContentFailure: (state, action) => {
      state.deleteLoading = false;
      state.deleteError = action.payload.error;
      state.deletingContentId = null;
    },
    toggleContentPublishedRequest: (state, action) => {
      state.togglePublishedLoading = true;
      state.togglePublishedError = null;
      state.togglingContentId = action.payload.contentId;
    },
    toggleContentPublishedSuccess: (state, action) => {
      const { contentId, published } = action.payload;
      state.togglePublishedLoading = false;
      state.togglePublishedError = null;
      state.togglingContentId = null;
      state.results = state.results.map(item =>
        String(item.id) === String(contentId) ? { ...item, published } : item
      );
    },
    toggleContentPublishedFailure: (state, action) => {
      state.togglePublishedLoading = false;
      state.togglePublishedError = action.payload.error;
      state.togglingContentId = null;
    }
  }
});

export const reducer = slice.reducer;

function parseSearchPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, nextCursor: null, total: data.length };
  }
  return {
    items: Array.isArray(data?.contentList)
      ? data.contentList
      : Array.isArray(data?.searchResults)
        ? data.searchResults
        : [],
    nextCursor: data?.nextCursor ?? null,
    total: typeof data?.total === 'number' ? data.total : 0
  };
}

function parseEditorsPayload(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.editors)) {
    return data.editors;
  }
  if (Array.isArray(data?.items)) {
    return data.items;
  }
  return [];
}

function normalizeEditorItem(item, index) {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const id = item.id ?? item.userId ?? item.editorId ?? item.uuid ?? null;
  const fullName = [item.firstName, item.lastName].filter(Boolean).join(' ').trim();
  const name = item.name || item.fullName || item.displayName || fullName || item.email || '';
  if (id === null || id === undefined || !name) {
    return null;
  }
  return {
    key: String(id),
    value: id,
    label: name,
    sortLabel: String(name).toLowerCase(),
    index
  };
}

function normalizeContentItem(item) {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const rawLanguages = item.languages ?? item.language ?? item.locale ?? '';
  const normalizedLanguages = Array.isArray(rawLanguages)
    ? rawLanguages
    : typeof rawLanguages === 'string' && rawLanguages.trim()
      ? rawLanguages
          .split('|')
          .map(language => language.trim())
          .filter(Boolean)
      : [];

  return {
    ...item,
    date: item.creationDate || item.created || item.date || '',
    created: item.created || item.creationDate || '',
    market: item.market || '',
    published: typeof item.published === 'boolean' ? item.published : null,
    text: item.text || item.teaser || item.description || '',
    description: item.text || item.teaser || item.description || '',
    tags: Array.isArray(item.tags) ? item.tags : [],
    languages:
      normalizedLanguages.length > 0 && item.market
        ? normalizedLanguages
        : normalizedLanguages.length > 0
          ? normalizedLanguages
          : item.market
            ? [item.market]
            : []
  };
}

function resolveSearchOrder(selectedSortBy) {
  if (!selectedSortBy) {
    return 'latest';
  }
  const normalized = String(selectedSortBy).trim().toLowerCase();
  const supportedOrders = ['relevant', 'latest', 'title', 'created', 'language'];
  return supportedOrders.includes(normalized) ? normalized : 'latest';
}

function normalizeKeywords(value) {
  if (Array.isArray(value)) {
    return value.map(keyword => String(keyword || '').trim()).filter(Boolean);
  }
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function toApiDate(value) {
  if (!value) {
    return null;
  }
  const casted = moment(value);
  return casted.isValid() ? casted.format('YYYY-MM-DD') : null;
}

export const getContents =
  ({ cursor = null, append = false, limit = 25 } = {}) =>
  async (dispatch, getState) => {
    const { getContents, getContentsSuccess, getContentsFailure } = slice.actions;
    try {
      dispatch(getContents({ append }));
      const { contents } = getState();
      const order = resolveSearchOrder(contents?.selectedSortBy);
      const dateFrom = toApiDate(contents?.dateFrom);
      const dateTo = toApiDate(contents?.dateTo);
      const editors = Array.isArray(contents?.selectedEditors)
        ? contents.selectedEditors.map(value => Number(value)).filter(value => Number.isFinite(value))
        : [];
      const keywords = normalizeKeywords(contents?.keyword);

      const searchParams = {
        ...(keywords.length > 0 ? { 'keywords[]': keywords } : {}),
        ...(dateFrom ? { 'date-from': dateFrom } : {}),
        ...(dateTo ? { 'date-to': dateTo } : {}),
        ...(typeof contents?.selectedStatus === 'boolean' ? { status: contents.selectedStatus } : {}),
        ...(editors.length > 0 ? { 'editors[]': editors } : {}),
        ...(contents?.selectedLanguage
          ? {
              'languages[]':
                resolveExtranetApiLanguageCode(contents.selectedLanguage) || contents.selectedLanguage
            }
          : {}),
        ...(contents?.onlyOwn ? { 'only-own': true } : {}),
        ...(order ? { order } : {}),
        ...(Number.isFinite(limit) ? { limit } : {}),
        ...(cursor ? { cursor } : {})
      };

      const response = await axios.get<ContentListApiResponse>(ENDPOINTS.GET_CONTENTS_ENDPOINT, { params: searchParams });
      const { items, nextCursor, total } = parseSearchPayload(response?.data);
      const nextItems = items.map(normalizeContentItem).filter(Boolean);
      dispatch(getContentsSuccess({ items: nextItems, nextCursor, total, append }));
    } catch (error) {
      dispatch(getContentsFailure(error?.message || 'Failed to load contents'));
    }
  };

export const getEditors = () => async dispatch => {
  const { getEditors, getEditorsSuccess, getEditorsFailure } = slice.actions;
  try {
    dispatch(getEditors());
    const response = await axios.get<EditorsResponse>(ENDPOINTS.GET_EDITORS_ENDPOINT);
    const items = parseEditorsPayload(response?.data)
      .map((item, index) => normalizeEditorItem(item, index))
      .filter(Boolean)
      .sort((a, b) => {
        if (a.sortLabel === b.sortLabel) return a.index - b.index;
        return a.sortLabel.localeCompare(b.sortLabel);
      })
      .map(({ key, value, label }) => ({ key, value, label }));
    dispatch(getEditorsSuccess(items));
  } catch (error) {
    dispatch(getEditorsFailure(error?.message || 'Failed to load editors'));
  }
};

function readRequestError(error, fallback) {
  if (typeof error === 'string' && error.trim()) return error;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  return fallback;
}

export const toggleContentPublished =
  ({ contentId, published, locale }) =>
  async dispatch => {
    const {
      toggleContentPublishedRequest,
      toggleContentPublishedSuccess,
      toggleContentPublishedFailure
    } = slice.actions;
    const nextPublished = Boolean(published);
    try {
      dispatch(toggleContentPublishedRequest({ contentId }));
      await axios.patch(
        `${ENDPOINTS.GET_CONTENT_POST_ENDPOINT}/${contentId}`,
        {
          contentPost: [
            {
              published: nextPublished,
              ...(locale ? { locale } : {})
            }
          ]
        },
        locale ? { acceptLanguage: locale } : undefined
      );
      dispatch(toggleContentPublishedSuccess({ contentId, published: nextPublished }));
      return { success: true, published: nextPublished };
    } catch (error) {
      const errorMessage = readRequestError(error, 'Failed to update content visibility');
      dispatch(toggleContentPublishedFailure({ error: errorMessage, contentId }));
      throw new Error(errorMessage);
    }
  };

export const deleteContent = contentId => async dispatch => {
  const { deleteContentRequest, deleteContentSuccess, deleteContentFailure } = slice.actions;
  try {
    dispatch(deleteContentRequest({ contentId }));
    await axios.delete(`${ENDPOINTS.GET_CONTENT_POST_ENDPOINT}/${contentId}`);
    await dispatch(deleteContentSuccess({ contentId }));
    return { success: true };
  } catch (error) {
    const errorMessage = error?.message || 'Failed to delete content';
    dispatch(deleteContentFailure({ error: errorMessage, contentId }));
    throw new Error(errorMessage);
  }
};

export const {
  setKeyword,
  setDateRange,
  setSelectedStatus,
  setSelectedEditors,
  setSelectedLanguage,
  setOnlyOwn,
  setSelectedSortBy,
  resetFilters
} = slice.actions;
